import { getLogger } from '@blueberry/observability'
import { delayWhileVisible, poll } from '@blueberry/utils/common'
import { Flow, SignupData } from '../types'

const logger = getLogger('signup/analytics/impact')

/**
 * Default tracker ID for signup events on Impact.
 */
const SIGNUP_TRACKER_ID = 61353

type ImpactConversionParams = {
  email: string // raw email; will be SHA-1 hashed here
}

declare global {
  interface Window {
    ire: (
      command: 'trackConversion',
      actionTrackerId: number,
      params: {
        orderId: string // same value as customerEmail
        customerId: string
        customerEmail: string // SHA-1 hex string per Impact spec
      },
      options?: { verifySiteDefinitionMatch: boolean },
    ) => void
  }
}

export const impact = {
  async trackStep(flow: Flow, step: number, data: SignupData) {
    // we currently only use impact for d2c
    if (flow !== 'd2c') return

    try {
      switch (step) {
        case 3:
          {
            const ire = await poll(() => window.ire)
            if (!ire) {
              logger.error('Retries exhausted waiting for Impact to initialize')
              return
            }

            const hashed = data.account?.email
              ? await sha1Hex(data.account?.email)
              : ''
            delayWhileVisible(() => {
              ire(
                'trackConversion',
                SIGNUP_TRACKER_ID,
                {
                  orderId: hashed,
                  customerId: '', // empty string is required by Impact
                  customerEmail: hashed,
                },
                { verifySiteDefinitionMatch: true },
              )
            }, 10000)
          }
          break
      }
    } catch (error) {
      logger.error('Error tracking impact step event:', error)
      return
    }
  },
}

/**
 * SHA-1 hash to hex string using Web Crypto.
 */
async function sha1Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input.trim().toLowerCase())
  const digest = await crypto.subtle.digest('SHA-1', data)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
